import { queryKeys } from "@/lib/queryKeys";
import { featureFlagsProvider } from "@/providers/feature-flags.provider";
import { useQuery } from "@tanstack/react-query";

export const ADMIN_DELETES_FLAG_KEY = "enable_admin_deletes";

// Gates a destructive UI affordance, so this fails closed: `enabled` stays
// false while loading, on error, or before data arrives — callers must treat
// isLoading as "not allowed yet", not just check `enabled`.
//
// refetchOnMount: "always" means every component that opens (a tab, a sheet,
// a table row) re-verifies the flag right then, instead of trusting a cached
// value from up to `staleTime` ago — an admin flipping the flag in another
// tab/window takes effect the next time a delete affordance is opened here,
// no page reload needed.
export function useFeatureFlagCheck(key: string = ADMIN_DELETES_FLAG_KEY) {
  const query = useQuery({
    queryKey: queryKeys.featureFlagCheck(key),
    queryFn: () => featureFlagsProvider.check(key),
    staleTime: 30_000,
    refetchOnMount: "always",
    retry: false,
    enabled: !!key,
  });

  return {
    enabled: query.data?.enabled ?? false,
    isLoading: query.isFetching,
    isError: query.isError,
  };
}
